import { useState } from "react";
import { CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

export interface SessionLogEntry {
  ts: string;
  message: string;
  tone: "success" | "warning" | "danger" | "muted";
}

interface Props {
  entries: string[];
  sessionEntries?: SessionLogEntry[];
}

function shortTs(iso: string) {
  return iso.slice(11, 19); // HH:MM:SS
}

export function CollectionLog({ entries, sessionEntries = [] }: Props) {
  const [expanded, setExpanded] = useState(false);
  const limit = 30;
  const reversed = [...entries].reverse();
  const shown = expanded ? reversed : reversed.slice(0, limit);
  const hidden = reversed.length - shown.length;

  const hasAnything = sessionEntries.length > 0 || entries.length > 0;
  if (!hasAnything) return null;

  return (
    <Card padding="none">
      <div className="px-4 py-2 border-b border-border text-xs font-medium text-text-muted">
        Dziennik
      </div>

      {/* Session entries — live actions from this browser session */}
      {sessionEntries.length > 0 && (
        <ul className="divide-y divide-border border-b border-border">
          {[...sessionEntries].reverse().map((entry, idx) => (
            <li key={idx} className={cn(
              "px-4 py-2 text-xs flex items-start gap-2",
              entry.tone === "success" && "text-success",
              entry.tone === "warning" && "text-warning",
              entry.tone === "danger" && "text-danger",
              entry.tone === "muted" && "text-text-muted",
            )}>
              {entry.tone === "success" && <CheckCircle2 className="size-3.5 mt-0.5 shrink-0" />}
              {entry.tone === "danger" && <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />}
              {(entry.tone === "warning" || entry.tone === "muted") && <Clock className="size-3.5 mt-0.5 shrink-0" />}
              <span className="flex-1">{entry.message}</span>
              <span className="font-mono text-text-subtle shrink-0">{shortTs(entry.ts)}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Server log — auto_collection_log from DynamoDB */}
      {entries.length > 0 && (
        <>
          {sessionEntries.length > 0 && (
            <div className="px-4 py-1.5 text-xs text-text-subtle border-b border-border bg-bg-subtle">
              Historia serwera
            </div>
          )}
          <ul className="divide-y divide-border max-h-[360px] overflow-y-auto">
            {shown.map((line, idx) => {
              const isError = /ERROR/i.test(line);
              return (
                <li key={idx} className={cn(
                  "px-4 py-1.5 text-xs font-mono flex items-start gap-2",
                  isError ? "text-danger" : "text-text-muted",
                )}>
                  {isError
                    ? <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />
                    : <CheckCircle2 className="size-3.5 mt-0.5 shrink-0 text-success" />}
                  <span className="break-all">{line}</span>
                </li>
              );
            })}
          </ul>
          {hidden > 0 && (
            <button onClick={() => setExpanded(true)}
                    className="w-full px-4 py-2 text-xs text-text-muted hover:text-text border-t border-border">
              Pokaż więcej ({hidden})
            </button>
          )}
        </>
      )}
    </Card>
  );
}
